import type { BusinessHours, DayHours } from './businessHours';

/**
 * Israeli holidays, from Hebcal's Israel calendar (hebcal.com, CC BY 4.0), reduced to the four kinds of day that change
 * opening hours. Everything else Hebcal lists (Chanukah, Purim, memorial days…) is an ordinary working day.
 */
export type HolidayKind = 'yomtov' | 'erev' | 'cholhamoed' | 'national';
export type Holiday = { date: string; kind: HolidayKind; name: string }; // date: YYYY-MM-DD (Israel)

/** What a kind of day does to opening hours. */
export type HolidayRule = 'closed' | 'friday' | 'regular';
export type HolidayRules = Record<HolidayKind, HolidayRule>;

export const HOLIDAY_KINDS: Array<{ kind: HolidayKind; label: string; example: string }> = [
  { kind: 'yomtov', label: 'יום טוב', example: 'ראש השנה, יום כיפור, סוכות, פסח, שבועות' },
  { kind: 'erev', label: 'ערב חג', example: 'ערב ראש השנה, ערב פסח…' },
  { kind: 'cholhamoed', label: 'חול המועד', example: 'סוכות ופסח' },
  { kind: 'national', label: 'יום העצמאות', example: '' },
];
export const HOLIDAY_RULE_LABELS: Record<HolidayRule, string> = { closed: 'סגור', friday: 'שעות של יום שישי', regular: 'שעות רגילות' };

/** Pro Car's: closed on yom tov and Independence Day, Friday hours on the eve, regular hours in chol hamoed. */
export const DEFAULT_HOLIDAY_RULES: HolidayRules = { yomtov: 'closed', erev: 'friday', cholhamoed: 'regular', national: 'closed' };

export function parseHolidayRules(v: unknown): HolidayRules {
  const r = (v ?? {}) as Partial<Record<HolidayKind, unknown>>;
  const ok = (x: unknown): x is HolidayRule => x === 'closed' || x === 'friday' || x === 'regular';
  return {
    yomtov: ok(r.yomtov) ? r.yomtov : DEFAULT_HOLIDAY_RULES.yomtov,
    erev: ok(r.erev) ? r.erev : DEFAULT_HOLIDAY_RULES.erev,
    cholhamoed: ok(r.cholhamoed) ? r.cholhamoed : DEFAULT_HOLIDAY_RULES.cholhamoed,
    national: ok(r.national) ? r.national : DEFAULT_HOLIDAY_RULES.national,
  };
}

/** One item of Hebcal's JSON (only the fields we read). */
export type HebcalItem = { date: string; title: string; hebrew?: string; yomtov?: boolean; subcat?: string };

// Hebcal writes typographic apostrophes ("CH’’M", "Yom HaAtzma’ut"); accept plain ones too.
const EREV = /^Erev (Rosh Hashana|Yom Kippur|Sukkot|Pesach|Shavuot)\b/;
const CHOL_HAMOED = /CH['’]{2}M|Hoshana Raba/;
const INDEPENDENCE = /^Yom HaAtzma['’]ut$/;

/** Hebcal items → the days that matter for opening hours. */
export function classifyHebcal(items: HebcalItem[]): Holiday[] {
  const out: Holiday[] = [];
  for (const i of items) {
    const date = i.date.slice(0, 10);
    const name = i.hebrew ?? i.title;
    const kind: HolidayKind | null = i.yomtov ? 'yomtov'
      : EREV.test(i.title) ? 'erev'
      : CHOL_HAMOED.test(i.title) ? 'cholhamoed'
      : INDEPENDENCE.test(i.title) ? 'national'
      : null;
    if (kind) out.push({ date, kind, name });
  }
  return out;
}

/**
 * Per-date opening hours that replace the weekly schedule: null = closed, Friday's hours for the eve, and nothing at all
 * for a rule of "regular". A holiday falling on a closed weekday (Saturday) never opens it.
 */
export function holidayOverrides(holidays: Holiday[], rules: HolidayRules, bh: BusinessHours): Map<string, DayHours> {
  const m = new Map<string, DayHours>();
  for (const h of holidays) {
    const rule = rules[h.kind];
    if (rule === 'regular') continue;
    const weekday = new Date(`${h.date}T00:00:00Z`).getUTCDay();
    if (!bh.week[weekday]) continue; // already closed that day
    m.set(h.date, rule === 'closed' ? null : bh.week[5]);
  }
  return m;
}
